/**
 * <toc-navigation> — Sommaire (table des matières) de la page produit.
 *
 * Transforme les ancres `_navigation-link` en trois vues synchronisées :
 *   - un rail vertical flottant (desktop large, position: fixed dans la gouttière),
 *   - une barre sticky horizontale (mobile + desktop intermédiaire),
 *   - un tiroir plein écran « Sommaire du dossier ».
 *
 * Fonctionnalités :
 *   - scroll-spy : surligne la section en cours de lecture (la plus basse dont
 *     le haut a dépassé la ligne header + marge),
 *   - smooth-scroll au clic avec offset = hauteur du header sticky (mesurée live),
 *   - indicateur mobile « § <libellé> » + compteur romain « II / VIII »,
 *   - tiroir ouvrable (lock du scroll, fermeture via croix / Échap / clic overlay / entrée).
 *
 * Aucune dépendance externe (pas d'Alpine). Mêmes conventions que sticky-add-to-cart.js.
 */
class TocNavigation extends HTMLElement {
  #links = [];
  #activeId = null;
  #headerEl = null;
  #headerHeight = 0;
  #resizeObserver = null;
  #scrollPending = false;
  // Reveal-on-scroll : la barre reste masquée tant que la page n'est pas scrollée
  // au-delà de ce seuil (px). Évite le bandeau vide en haut au chargement.
  #revealThreshold = 64;

  connectedCallback() {
    this.source = this.querySelector('[data-ref="toc-source"]');
    if (!this.source) return;

    this.rail = this.querySelector('[data-ref="toc-rail"]');
    this.barTrack = this.querySelector('[data-ref="toc-bar-track"]');
    this.drawer = this.querySelector('[data-ref="toc-drawer"]');
    this.drawerList = this.querySelector('[data-ref="toc-drawer-list"]');
    this.indicator = this.querySelector('[data-ref="toc-indicator"]');
    this.indicatorLabel = this.querySelector('[data-ref="toc-indicator-label"]');
    this.indicatorCounter = this.querySelector('[data-ref="toc-indicator-counter"]');
    this.drawerClose = this.querySelector('[data-ref="toc-drawer-close"]');
    this.drawerOverlay = this.querySelector('[data-ref="toc-drawer-overlay"]');
    this.bar = this.querySelector('[data-ref="toc-bar"]');

    this.#headerEl = document.querySelector('header-component');

    this.#buildViews();
    this.#bindEvents();
    this.#measureHeader();
    this.#measureBar();

    // Repositionne quand la hauteur du header change (annonce, resize…).
    this.#resizeObserver = new ResizeObserver(() => {
      this.#measureHeader();
      this.#measureBar();
      this.#computeActive();
    });
    if (this.#headerEl) this.#resizeObserver.observe(this.#headerEl);

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('load', this.#onResize);

    // État initial du reveal (gère un reload déjà scrollé ou une arrivée sur ancre).
    this.#updateReveal();

    // Passes initiales : laisse le layout + reflow polices/images se stabiliser.
    requestAnimationFrame(() => {
      this.#measureBar();
      this.#computeActive();
    });
    setTimeout(() => {
      this.#measureBar();
      this.#computeActive();
    }, 300);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#onResize);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#resizeObserver?.disconnect();
    document.body.classList.remove('overflow-hidden');
  }

  /** Mesure la hauteur du header sticky → variable CSS --toc-top + offset de scroll. */
  #measureHeader = () => {
    this.#headerHeight = this.#headerEl ? Math.round(this.#headerEl.getBoundingClientRect().height) : 0;
    this.style.setProperty('--toc-top', `${this.#headerHeight}px`);
  };

  /**
   * Mesure la hauteur de la barre sticky → variable CSS --toc-bar-height.
   * La barre est en position: fixed (sinon elle ne colle pas, car sa section
   * parente est trop courte) ; cette hauteur réserve l'espace dans le flux pour
   * que le contenu ne passe pas sous la barre. Vaut 0 quand la barre est masquée
   * (mode rail desktop), donc aucun espace réservé inutilement.
   */
  #measureBar = () => {
    if (!this.bar) return;
    const height = this.bar.getBoundingClientRect().height;
    this.style.setProperty('--toc-bar-height', `${Math.round(height)}px`);
  };

  #onResize = () => {
    this.#measureHeader();
    this.#measureBar();
    this.#computeActive();
  };

  /**
   * Offset haut occupé par les éléments fixes : header + barre sticky (si visible)
   * + petite marge. La barre vaut 0 quand elle est masquée (mode rail desktop),
   * donc l'offset = hauteur header seule sur grand écran.
   */
  #topOffset() {
    const barHeight = this.bar ? this.bar.getBoundingClientRect().height : 0;
    return this.#headerHeight + barHeight + 12;
  }

  /**
   * Ligne de détection du scroll-spy : sous les éléments fixes + une bande de
   * tolérance (~20 % du viewport). La bande absorbe les décalages de layout
   * (images lazy qui se chargent et repoussent les sections après le scroll),
   * pour qu'une section atteinte au clic soit bien marquée active.
   */
  #spyLine() {
    return this.#topOffset() + Math.round(window.innerHeight * 0.2);
  }

  /** Lit les ancres source et construit rail / barre / tiroir. */
  #buildViews() {
    const anchors = Array.from(this.source.querySelectorAll('a[data-toc-target]'));
    const total = anchors.length;

    anchors.forEach((anchor, index) => {
      const id = anchor.dataset.tocTarget;
      const label = (anchor.querySelector('.navigation__title')?.textContent || anchor.textContent || '').trim();
      const ordinal = index + 1;
      const roman = this.#roman(ordinal);
      // Nom = label sans le préfixe romain « I - », « VIII – », etc.
      const name = label.replace(/^\s*[IVXLCDM]+\s*[-–—]\s*/i, '').trim() || label;

      const railEl = this.#makeRailEntry(id, roman, name);
      const barEl = this.#makeEntry(id, label, 'toc-item--bar');
      const drawerEl = this.#makeEntry(id, label, 'toc-item--drawer');

      this.rail?.appendChild(railEl);
      this.barTrack?.appendChild(barEl);
      this.drawerList?.appendChild(drawerEl);

      this.#links.push({ id, label, ordinal, total, railEl, barEl, drawerEl });
    });
  }

  /** Crée un <a> d'entrée TOC (barre d'accent + libellé). */
  #makeEntry(id, label, variant) {
    const link = document.createElement('a');
    link.href = `#${id}`;
    link.className = `toc-item ${variant}`;
    link.dataset.tocTarget = id;
    link.innerHTML = '<span class="toc-item__bar" aria-hidden="true"></span><span class="toc-item__label"></span>';
    link.querySelector('.toc-item__label').textContent = label;
    link.addEventListener('click', this.#onEntryClick);
    return link;
  }

  /** Crée une entrée du rail desktop : chiffre romain (au-dessus) + nom de section. */
  #makeRailEntry(id, roman, name) {
    const link = document.createElement('a');
    link.href = `#${id}`;
    link.className = 'toc-item toc-item--rail';
    link.dataset.tocTarget = id;
    link.innerHTML =
      '<span class="toc-item__bar" aria-hidden="true"></span>' +
      '<span class="toc-item__num"></span><span class="toc-item__label"></span>';
    link.querySelector('.toc-item__num').textContent = roman;
    link.querySelector('.toc-item__label').textContent = name;
    link.addEventListener('click', this.#onEntryClick);
    return link;
  }

  #bindEvents() {
    this.indicator?.addEventListener('click', this.#toggleDrawer);
    this.drawerClose?.addEventListener('click', this.#closeDrawer);
    this.drawerOverlay?.addEventListener('click', this.#closeDrawer);
    document.addEventListener('keydown', this.#onKeydown);
  }

  /** Throttle scroll via rAF. */
  #onScroll = () => {
    if (this.#scrollPending) return;
    this.#scrollPending = true;
    requestAnimationFrame(() => {
      this.#scrollPending = false;
      this.#updateReveal();
      this.#computeActive();
    });
  };

  /** Affiche la barre sticky dès que la page est scrollée au-delà du seuil. */
  #updateReveal = () => {
    this.toggleAttribute('data-toc-revealed', window.scrollY > this.#revealThreshold);
  };

  /** Section active = la plus basse dont le haut a dépassé la ligne (header + marge). */
  #computeActive = () => {
    if (!this.#links.length) return;
    const line = this.#spyLine();
    let active = this.#links[0].id;
    for (const link of this.#links) {
      const el = document.getElementById(link.id);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= line) active = link.id;
    }
    this.#setActive(active);
  };

  #setActive(id) {
    if (!id || id === this.#activeId) return;
    this.#activeId = id;

    this.#links.forEach((link) => {
      const on = link.id === id;
      link.railEl.classList.toggle('navigation-item--selected', on);
      link.barEl.classList.toggle('navigation-item--selected', on);
      link.drawerEl.classList.toggle('navigation-item--selected', on);

      if (on) {
        if (this.indicatorLabel) this.indicatorLabel.textContent = `§ ${link.label}`;
        if (this.indicatorCounter) {
          this.indicatorCounter.textContent = `${this.#roman(link.ordinal)} / ${this.#roman(link.total)}`;
        }
        // Garde l'entrée active visible dans la barre horizontale (no-op si masquée).
        link.barEl.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    });
  }

  #onEntryClick = (event) => {
    const id = event.currentTarget?.dataset.tocTarget;
    const target = id ? document.getElementById(id) : null;
    if (!target) return; // lien sans cible (ex. preview) → comportement par défaut
    event.preventDefault();
    const y = target.getBoundingClientRect().top + window.scrollY - this.#topOffset();
    this.#closeDrawer();
    window.scrollTo({ top: y, behavior: 'smooth' });
    this.#setActive(id);
  };

  #toggleDrawer = () => {
    if (this.drawer?.getAttribute('aria-hidden') === 'false') this.#closeDrawer();
    else this.#openDrawer();
  };

  #openDrawer = () => {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.indicator?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
  };

  #closeDrawer = () => {
    if (!this.drawer || this.drawer.getAttribute('aria-hidden') === 'true') return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.indicator?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.drawer?.getAttribute('aria-hidden') === 'false') this.#closeDrawer();
  };

  /** Convertit 1..N en chiffres romains (compteur « II / VIII »). */
  #roman(value) {
    const table = [
      [10, 'X'],
      [9, 'IX'],
      [5, 'V'],
      [4, 'IV'],
      [1, 'I'],
    ];
    let result = '';
    let n = value;
    for (const [amount, symbol] of table) {
      while (n >= amount) {
        result += symbol;
        n -= amount;
      }
    }
    return result || 'I';
  }
}

if (!customElements.get('toc-navigation')) {
  customElements.define('toc-navigation', TocNavigation);
}
